/**
 * Where a parent goes next.
 *
 * The dashboard answers three questions — where is my child, what do I owe, what is on this
 * week — and then, having answered them, used to leave the parent on a page with one way off
 * it: the top nav. Everything else the portal can do for a family lives two or three taps
 * deep behind Account, and the one link that had escaped was hanging off the week grid's
 * header, where a navigation link sat dressed as part of a timetable.
 *
 * This is the one block on the page that is allowed to be colourful, and the reason is worth
 * writing down, because the rest of the dashboard reserves colour for state: nothing here is
 * a state. These are doors, not facts — there is no green that could be mistaken for
 * "checked in" and no amber that could be mistaken for "overdue", so a tinted tile buys
 * scannability at no cost to the vocabulary the cards above depend on. It also earns the
 * block its place at the bottom: a flat grey list down there would be read by nobody.
 *
 * Each tile carries a line saying what it is for. Four labelled destinations with no
 * explanation would be a second nav bar, and the portal already has one of those.
 */

import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { TYPE_ATTRIBUTE, TYPE_OBJECT, TYPE_SECTION_WIDE } from '../typeScale';
import { useLabelParts } from '../../config/useLabels';
import type { LabelKey } from '../../config/labels';

/** The four tints, written out rather than composed — Tailwind reads source, not variables. */
const TONES = {
  amber: {
    tile: 'border-amber-200/70 bg-amber-50 group-hover:border-amber-300 group-hover:bg-amber-100/70',
    icon: 'bg-amber-100 text-amber-700 group-hover:bg-amber-200/80',
  },
  violet: {
    tile: 'border-violet-200/70 bg-violet-50 group-hover:border-violet-300 group-hover:bg-violet-100/70',
    icon: 'bg-violet-100 text-violet-700 group-hover:bg-violet-200/80',
  },
  emerald: {
    tile: 'border-emerald-200/70 bg-emerald-50 group-hover:border-emerald-300 group-hover:bg-emerald-100/70',
    icon: 'bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200/80',
  },
  sky: {
    tile: 'border-sky-200/70 bg-sky-50 group-hover:border-sky-300 group-hover:bg-sky-100/70',
    icon: 'bg-sky-100 text-sky-700 group-hover:bg-sky-200/80',
  },
} as const;

export type QuickLinkTone = keyof typeof TONES;

export interface QuickLink {
  /** Bilingual label key, so these read the same as the nav entries they shortcut to. */
  labelKey: LabelKey;
  to: string;
  /** One line on why a parent would tap it. */
  detail: string;
  icon: LucideIcon;
  tone: QuickLinkTone;
}

export function QuickLinksSection({ links, className = '' }: { links: QuickLink[]; className?: string }) {
  const label = useLabelParts();
  const heading = label('header.quickActions');

  if (links.length === 0) return null;

  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 ${className}`}
      aria-label={heading.en}
    >
      <h2 className={`${TYPE_SECTION_WIDE} mb-3`}>
        {heading.zh && <span className="mr-1.5 font-normal normal-case tracking-normal">{heading.zh}</span>}
        {heading.en}
      </h2>

      {/* Two across wherever there is room for two. A single column of four wide tiles reads
          as a menu; paired, they read as a set of doors, which is what they are. */}
      <div className="grid gap-2.5 sm:grid-cols-2">
        {links.map(link => {
          const name = label(link.labelKey);
          const tone = TONES[link.tone];
          const Icon = link.icon;
          return (
            <Link
              key={link.to}
              to={link.to}
              className={`group flex items-center gap-3 rounded-xl border p-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${tone.tile}`}
              style={{ textDecoration: 'none' }}
            >
              <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg transition-colors ${tone.icon}`}>
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className={`${TYPE_OBJECT} block truncate`}>
                  {name.zh && <span className="mr-1.5 font-normal">{name.zh}</span>}
                  {name.en}
                </span>
                {/* Darker than the page's grey attribute step: on a tinted ground, gray-500
                    is the first thing to disappear. */}
                <span className={`${TYPE_ATTRIBUTE} block text-gray-600`}>{link.detail}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
